import type { NextAction, RefusedStep } from "../api/types.js";

/**
 * The words of the states the design review rewrote (DCTA-118 review, UX spec v3 §F.2).
 *
 * The voice rule decides the surface (`surfaces.ts`); this file holds the words the APP owns: busy,
 * unavailable, offline, "Don't send", the add-payee chip under an R-U1 question. (DCTA-124: the
 * hand-over lines before a refusal card went with the card — Umakhan's ruling, 5 Oct: a refusal is the
 * assistant's message, in the bank's words, so nothing hands over to it.) Every
 * sentence the BANK sends is printed exactly as it sent it — DCTA-124 retired the interim bridge of
 * DCTA-120 (the bank says §F.2's words at source since DCTA-123), so the screen and the audit record
 * carry the same words (Q5), a record written by an older bank included.
 */
export const SAY = {
  busy: "I'm busy for a moment. Nothing was sent — try again in a few seconds.",
  unavailable: "I can't take requests right now. Nothing was sent — try again in a little while.",
  offline: "I can't reach the bank right now. Nothing was sent — try again in a moment.",
  /** §F.2 6h: the answer that ends a name-divergence question, and what the assistant says to it. */
  dontSend: "Don't send",
  notSent: "Nothing was sent.",
  /**
   * §F.2 8b: the chip under a question about someone no saved payee names — row 5b's chip, unchanged.
   * The R-U1 question carries no next step of its own, so these are the app's words (5b's, as the bank
   * sends them on the pointer).
   */
  addPayee: { label: "Add a payee", small: "New payees can be paid 12 hours after they're added" },
  /**
   * DCTA-143 (red-team P2 finding 2; Claude's brief — no §F row yet, for ux-designer): the second line of a payee chip
   * the bank marks `never_paid` (DCTA-137 offers those payees last). The bank sends the flag, never words, so these
   * are the app's.
   */
  newPayee: "New payee · never paid",
} as const;

/** What a refusal card says: its lead, one next step, at most one rule id, its one action. */
export interface RefusalWords {
  readonly lead: string;
  readonly next?: string;
  readonly rule?: string;
  readonly action?: { readonly label: string; readonly action: NextAction };
}

/**
 * Rule ids a customer is never shown (OD-05, tipping-off): a watchlist match (§F.2 6k) and a payment
 * pattern (11b). The bank withholds both from the wire (DCTA-123 B5, B12); this is the floor if one
 * ever reaches the client. The audit record keeps them.
 */
const WITHHELD: ReadonlySet<string> = new Set(["POL-SCREENING-HIT", "POL-STRUCTURING"]);

/** The one rule id a card may show: the first the bank names — none when it names a withheld one. */
export function shownRule(ruleIds: readonly string[]): string | undefined {
  return ruleIds.some((id) => WITHHELD.has(id)) ? undefined : ruleIds[0];
}

/**
 * A refused step in the words the card prints (§F.2 rows 1, 6k, 11): the bank's sentence as the lead,
 * its first next step, its first action — exactly as sent, nothing added — and at most one rule id
 * (`shownRule`; audit hook 3).
 */
export function refusalWords(r: RefusedStep): RefusalWords {
  const next = r.next_steps?.[0];
  const action = r.next_actions?.[0];
  const rule = shownRule(r.rule_ids);
  return {
    lead: r.customer_message,
    ...(next === undefined ? {} : { next }),
    ...(rule === undefined ? {} : { rule }),
    ...(action === undefined ? {} : { action }),
  };
}
